import {
  findCasesByProjectId,
  findCaseDetailById,
  updateChecks,
  updateCaseMetadata,
} from '../mock-data/cases.mock';
import {
  createMockResponse,
  createMockErrorResponse,
} from '../mock.interceptor';

import type {
  CaseListApiResponse,
  CaseDetailApiResponse,
  CheckUpdateApiRequest,
} from '../../features/cases/cases.models';
import type { HttpResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';

/**
 * /projects/:projectId/cases* へのリクエストをハンドリング
 * path はテナントプレフィックス除去済みの短縮パス
 */
export function handleCasesRequest(
  method: string,
  path: string,
  body: unknown,
): Observable<HttpResponse<unknown>> | null {
  // PATCH /projects/:projectId/cases/:caseId/checks - チェック一括更新（順序注意: /cases/:id より先に評価）
  const updateChecksMatch =
    /^\/projects\/([^/]+)\/cases\/([^/]+)\/checks$/.exec(path);
  if (method === 'PATCH' && updateChecksMatch) {
    const projectId = updateChecksMatch[1];
    const caseId = updateChecksMatch[2];
    return handleUpdateChecks(
      projectId,
      caseId,
      body as CheckUpdateApiRequest[],
    );
  }

  // PATCH /projects/:projectId/cases/:caseId/metadata - メタデータ更新（順序注意: /cases/:id より先に評価）
  const updateMetadataMatch =
    /^\/projects\/([^/]+)\/cases\/([^/]+)\/metadata$/.exec(path);
  if (method === 'PATCH' && updateMetadataMatch) {
    const projectId = updateMetadataMatch[1];
    const caseId = updateMetadataMatch[2];
    return handleUpdateCaseMetadata(projectId, caseId, body);
  }

  // GET /projects/:projectId/cases/:caseId - 審査対象詳細取得
  const getCaseDetailMatch = /^\/projects\/([^/]+)\/cases\/([^/]+)$/.exec(path);
  if (method === 'GET' && getCaseDetailMatch) {
    const projectId = getCaseDetailMatch[1];
    const caseId = getCaseDetailMatch[2];
    return handleGetCaseDetail(projectId, caseId);
  }

  // GET /projects/:projectId/cases - 審査対象一覧取得
  const getCasesMatch = /^\/projects\/([^/]+)\/cases$/.exec(path);
  if (method === 'GET' && getCasesMatch) {
    const projectId = getCasesMatch[1];
    return handleGetCases(projectId);
  }

  return null;
}

/**
 * GET /api/v0/tenants/:tenantId/projects/:projectId/cases - 審査対象一覧取得
 * cursor pagination 形式で返す
 */
function handleGetCases(
  projectId: string,
): Observable<HttpResponse<CaseListApiResponse>> {
  const items = findCasesByProjectId(projectId);
  return createMockResponse<CaseListApiResponse>({ items, nextCursor: null });
}

/**
 * GET /api/v0/tenants/:tenantId/projects/:projectId/cases/:caseId - 審査対象詳細取得
 * api-serverと同じくオブジェクトを直接返す
 */
function handleGetCaseDetail(
  projectId: string,
  caseId: string,
): Observable<HttpResponse<CaseDetailApiResponse | { message: string }>> {
  const detail = findCaseDetailById(projectId, caseId);
  if (!detail) {
    return createMockErrorResponse('審査対象が見つかりません', 404);
  }
  return createMockResponse(detail);
}

/**
 * PATCH /api/v0/tenants/:tenantId/projects/:projectId/cases/:caseId/checks - チェック一括更新
 * api-server準拠: 配列を直接受け取り、CaseDetailApiResponse を返す
 */
function handleUpdateChecks(
  projectId: string,
  caseId: string,
  body: CheckUpdateApiRequest[],
): Observable<HttpResponse<CaseDetailApiResponse | { message: string }>> {
  const response = updateChecks(projectId, caseId, body);
  if (!response) {
    return createMockErrorResponse('審査対象が見つかりません', 404);
  }
  return createMockResponse(response);
}

/**
 * PATCH /api/v0/tenants/:tenantId/projects/:projectId/cases/:caseId/metadata - メタデータ更新
 * api-server準拠: mark/memo を更新し、null を返す
 */
function handleUpdateCaseMetadata(
  projectId: string,
  caseId: string,
  body: unknown,
): Observable<HttpResponse<null | { message: string }>> {
  const success = updateCaseMetadata(projectId, caseId, body);
  if (!success) {
    return createMockErrorResponse('審査対象が見つかりません', 404);
  }
  return createMockResponse(null);
}
